import React from 'react';
import { clsx } from 'clsx';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  elevation?: number;
  variant?: 'elevation' | 'outlined';
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ elevation = 1, variant = 'elevation', className, children, ...props }, ref) => {
    const classes = clsx(
      'bg-white rounded-md overflow-hidden',
      {
        'shadow-sm': variant === 'elevation' && elevation === 1,
        'shadow': variant === 'elevation' && elevation === 2,
        'shadow-md': variant === 'elevation' && elevation === 3,
        'shadow-lg': variant === 'elevation' && elevation === 4,
        'shadow-xl': variant === 'elevation' && elevation === 5,
        'shadow-2xl': variant === 'elevation' && elevation === 6,
        'border border-gray-200': variant === 'outlined',
      },
      className
    );

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: 'none' | 'small' | 'medium' | 'large';
}

export const CardContent = React.forwardRef<HTMLDivElement, CardContentProps>(
  ({ padding = 'medium', className, children, ...props }, ref) => {
    const classes = clsx(
      {
        'p-0': padding === 'none',
        'p-3': padding === 'small',
        'p-4': padding === 'medium',
        'p-6': padding === 'large',
      },
      className
    );

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

CardContent.displayName = 'CardContent';

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  avatar?: React.ReactNode;
}

export const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, subtitle, action, avatar, className, children, ...props }, ref) => {
    const classes = clsx(
      'flex items-start justify-between p-4 border-b border-gray-200',
      className
    );

    return (
      <div ref={ref} className={classes} {...props}>
        <div className="flex items-center gap-3 flex-1">
          {avatar && <div className="flex-shrink-0">{avatar}</div>}
          <div className="flex-1 min-w-0">
            {title && (
              <h3 className="text-lg font-semibold text-gray-900 truncate">
                {title}
              </h3>
            )}
            {subtitle && (
              <p className="text-sm text-gray-500 mt-1">
                {subtitle}
              </p>
            )}
            {children}
          </div>
        </div>
        {action && <div className="flex-shrink-0 ml-4">{action}</div>}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

export interface CardActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'left' | 'center' | 'right';
}

export const CardActions = React.forwardRef<HTMLDivElement, CardActionsProps>(
  ({ align = 'right', className, children, ...props }, ref) => {
    const classes = clsx(
      'flex gap-2 p-4 border-t border-gray-200',
      {
        'justify-start': align === 'left',
        'justify-center': align === 'center',
        'justify-end': align === 'right',
      },
      className
    );

    return (
      <div ref={ref} className={classes} {...props}>
        {children}
      </div>
    );
  }
);

CardActions.displayName = 'CardActions';

export default Card;

